import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Plane,
  Map,
  Layers,
  Mountain,
  Ruler,
  Globe2,
  MapPin,
  Building2,
  Box,
  Radar,
  Camera,
  Sprout,
  ArrowUpRight,
  ArrowRight,
} from "lucide-react";
import { SERVICES } from "../../lib/constants";

const ICONS = {
  plane: Plane,
  map: Map,
  layers: Layers,
  mountain: Mountain,
  ruler: Ruler,
  globe: Globe2,
  pin: MapPin,
  crane: Building2,
  cube: Box,
  radar: Radar,
  camera: Camera,
  sprout: Sprout,
};

export const Services = () => {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-24 md:py-32 bg-black"
    >
      <div className="absolute inset-0 tb-grid-dense opacity-40 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14">
          <div>
            <div className="text-[11px] tracking-[0.3em] uppercase text-[#3DC0AF] font-mono font-bold">
              / SERVICES
            </div>
            <h2 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-display font-black tracking-tighter leading-[0.95] max-w-3xl">
              A full stack of geospatial services.
            </h2>
          </div>
          <p className="text-zinc-400 max-w-md text-base leading-relaxed">
            From airborne surveys to construction progress and asset
            management programs — a full stack of specialized offerings, one
            accountable team.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5 auto-rows-[minmax(200px,auto)]">
          {SERVICES.map((s, i) => {
            const Icon = ICONS[s.icon] || Map;
            const isFeatured = s.featured;

            const cardInner = (
              <>
                {isFeatured && (
                  <>
                    <div className="pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full bg-[#3DC0AF]/25 blur-3xl" />
                    <div className="pointer-events-none absolute inset-0 tb-grid-dense opacity-[0.18]" />
                  </>
                )}
                <div className="relative flex items-start justify-between">
                  <span
                    className={`inline-flex items-center justify-center rounded-lg transition-all ${
                      isFeatured
                        ? "h-12 w-12 border border-[#3DC0AF]/60 bg-[#3DC0AF]/15 text-[#5EE3CE] group-hover:bg-[#3DC0AF] group-hover:text-white"
                        : "h-10 w-10 border border-white/10 bg-black/40 text-[#5EE3CE] group-hover:text-white group-hover:bg-[#3DC0AF] group-hover:border-[#3DC0AF]"
                    }`}
                  >
                    <Icon className={isFeatured ? "h-6 w-6" : "h-5 w-5"} />
                  </span>
                  <div className="flex items-center gap-2">
                    {isFeatured && (
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-[#3DC0AF]/50 bg-black/60 backdrop-blur px-2.5 py-1 text-[10px] tracking-[0.2em] uppercase font-mono text-[#3DC0AF]">
                        <span className="h-1.5 w-1.5 rounded-full bg-[#3DC0AF] animate-pulse" />
                        New
                      </span>
                    )}
                    <span className="text-[10px] font-mono tracking-[0.25em] uppercase text-zinc-500">
                      {s.tag}
                    </span>
                  </div>
                </div>
                <h3 className="relative mt-8 text-xl md:text-2xl font-display font-bold tracking-tight">
                  {s.title}
                </h3>
                <p className="relative mt-3 text-sm text-zinc-400 leading-relaxed max-w-md">
                  {s.desc}
                </p>
              </>
            );

            const cardClass = `tb-card group relative overflow-hidden rounded-2xl p-6 md:p-7 ${
              isFeatured
                ? "border border-[#3DC0AF]/45 bg-gradient-to-br from-[#3DC0AF]/[0.18] via-[#0b1812] to-black tb-glow flex flex-col"
                : "border border-white/10 bg-gradient-to-b from-white/[0.04] to-white/[0.01]"
            } ${s.size}`;

            const hoverGlow = (
              <div className="pointer-events-none absolute -inset-px rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 [mask:linear-gradient(white,transparent_60%)]">
                <div className="absolute inset-0 rounded-2xl bg-[radial-gradient(circle_at_top_right,rgba(61,192,175,0.25),transparent_60%)]" />
              </div>
            );

            if (isFeatured) {
              return (
                <motion.div
                  key={s.title}
                  data-testid={`service-card-${i}`}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.6, delay: (i % 6) * 0.06 }}
                  className={cardClass}
                >
                  {cardInner}
                  <div className="relative mt-6 flex flex-wrap items-center gap-3">
                    <Link
                      to="/tbirdag"
                      data-testid={`service-card-${i}-tbirdag-link`}
                      className="inline-flex items-center gap-2 rounded-full bg-[#3DC0AF] hover:bg-[#00BA8B] px-4 py-2 text-sm text-white font-medium transition-colors"
                    >
                      <Sprout className="h-3.5 w-3.5" />
                      Visit TbirdAg
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                    <a
                      href="/#quote"
                      data-testid={`service-card-${i}-quote-link`}
                      className="inline-flex items-center gap-1.5 text-sm text-zinc-300 hover:text-white"
                    >
                      Request quote
                      <ArrowUpRight className="h-3.5 w-3.5" />
                    </a>
                  </div>
                  {hoverGlow}
                </motion.div>
              );
            }

            return (
              <motion.a
                key={s.title}
                href="/#quote"
                data-testid={`service-card-${i}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6, delay: (i % 6) * 0.06 }}
                className={cardClass}
              >
                {cardInner}
                <div className="relative mt-6 inline-flex items-center gap-2 text-sm text-zinc-300 group-hover:text-white">
                  <span>Request</span>
                  <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </div>
                {hoverGlow}
              </motion.a>
            );
          })}
        </div>
      </div>
    </section>
  );
};
